import { useState } from 'react';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

import SlidersIcon from 'components/icons/custom/Sliders';
import { useFeatureFlagsStore } from 'features/configuration/state/featureFlagsStore';
import { useUserWorksAtLevvr } from 'state/appState/appStateHooks';
import { isProduction } from 'utils/env';

import { useResetFeatureFlagsStore } from '../hooks/useResetFeatureFlagsStore';

export function FeatureFlagsMenu() {
  const toggleFeatureFlag = useFeatureFlagsStore((state) => state.toggleFeatureFlag);
  const locked = useFeatureFlagsStore((state) => state.locked);
  const toggleLock = useFeatureFlagsStore((state) => state.toggleLock);
  const featureFlags = useFeatureFlagsStore((state) => state.featureFlags || []);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const userWorksAtLevvr = useUserWorksAtLevvr();
  const reset = useResetFeatureFlagsStore();
  const disableAll = useFeatureFlagsStore((state) => state.disableAll);

  // This is a precaution to ensure that this menu is not shown to non-levvr users.
  if (!userWorksAtLevvr || isProduction) {
    return null;
  }

  const hasFeatureFlags = featureFlags.length > 0;

  return (
    <>
      <Tooltip title='Preview Features' placement='bottom'>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <SlidersIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id='feature-flags-menu'
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: !hasFeatureFlags ? 160 : 240 } } }}
        open={Boolean(anchorEl)}
      >
        {!hasFeatureFlags && (
          <MenuItem disabled>
            <ListItemText primary='No options' />
          </MenuItem>
        )}
        {hasFeatureFlags && (
          <>
            {featureFlags.map((featureFlag) => (
              <MenuItem
                key={featureFlag.name}
                id={`feature-flag-${featureFlag.name}-switch`}
                data-enabled={featureFlag.enabled}
                disabled={locked}
                onClick={() => toggleFeatureFlag(featureFlag.name)}
              >
                <ListItemText>{featureFlag.name}</ListItemText>
                <ListItemSecondaryAction>
                  <Switch checked={featureFlag.enabled} disabled={locked} color='secondary' size='small' />
                </ListItemSecondaryAction>
              </MenuItem>
            ))}

            <Divider />

            <MenuItem id='save-feature-flags-in-session-storage-button' onClick={toggleLock}>
              <ListItemText>Save in SessionStorage</ListItemText>
              <ListItemSecondaryAction>
                <Switch checked={locked} color='secondary' size='small' />
              </ListItemSecondaryAction>
            </MenuItem>

            <Divider />

            <MenuItem id='disable-all-feature-flags-button' disabled={locked} onClick={disableAll}>
              <ListItemText primary='Disable All' />
            </MenuItem>

            <MenuItem onClick={() => reset()}>
              <ListItemText primary='Reset' />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
